import { effectiveLoadG } from './load.js';
import { countsTowardVolume, isDropStage, isWarmup } from './sets.js';
import type { LoggedSet } from './types.js';

/**
 * Volume.
 *
 * Strength volume is `effective load × reps`, summed. Kept in **gram-reps**
 * (integers) rather than kilogram-reps so nothing is ever lost to floating
 * point across thousands of sets.
 *
 * Deliberately *not* computed for holds and cardio. Forcing a plank or a 4 km
 * run into a kilogram figure would produce a number that looks comparable and
 * is not.
 */

export interface VolumeResult {
  /** Total gram-reps. Divide by 1000 for kilogram-reps. */
  volumeGramReps: number;
  /** Sets that contributed. */
  countedSets: number;
  /** Included in `countedSets`; drop stages do count toward volume. */
  countedDropStages: number;
  excludedWarmups: number;
  excludedIncomplete: number;
  /** Sets skipped because their load could not be determined. */
  unknownLoadSets: number;
  /**
   * True when at least one set was skipped for unknown load. The total is then
   * a floor, not a fact, and callers must present it as approximate rather
   * than quietly under-reporting.
   */
  isPartial: boolean;
  totalReps: number;
}

const emptyResult = (): VolumeResult => ({
  volumeGramReps: 0,
  countedSets: 0,
  countedDropStages: 0,
  excludedWarmups: 0,
  excludedIncomplete: 0,
  unknownLoadSets: 0,
  isPartial: false,
  totalReps: 0,
});

/** Volume for one set, or null when it does not contribute. */
export function setVolumeGramReps(set: LoggedSet): number | null {
  if (!countsTowardVolume(set)) return null;
  if (set.reps === null || set.reps <= 0) return null;

  const load = effectiveLoadG(set);
  if (!load.known) return null;

  return load.value * set.reps;
}

/**
 * Strength volume across a set collection.
 *
 * Warm-ups and incomplete sets are excluded. Drop stages are included — they
 * were genuinely performed.
 */
export function strengthVolume(sets: readonly LoggedSet[]): VolumeResult {
  const result = emptyResult();

  for (const set of sets) {
    if (isWarmup(set)) {
      result.excludedWarmups++;
      continue;
    }
    if (!set.isCompleted) {
      result.excludedIncomplete++;
      continue;
    }
    if (set.loadType === 'none') {
      // Holds and cardio are measured elsewhere, not silently zeroed into here.
      continue;
    }

    const load = effectiveLoadG(set);
    if (!load.known) {
      result.unknownLoadSets++;
      continue;
    }
    if (set.reps === null || set.reps <= 0) continue;

    result.volumeGramReps += load.value * set.reps;
    result.totalReps += set.reps;
    result.countedSets++;
    if (isDropStage(set)) result.countedDropStages++;
  }

  result.isPartial = result.unknownLoadSets > 0;
  return result;
}

/** Total seconds held across completed, non-warm-up hold sets. */
export function holdDurationSeconds(sets: readonly LoggedSet[]): {
  totalSeconds: number;
  countedSets: number;
  excludedWarmups: number;
} {
  let totalSeconds = 0;
  let countedSets = 0;
  let excludedWarmups = 0;

  for (const set of sets) {
    if (isWarmup(set)) {
      excludedWarmups++;
      continue;
    }
    if (!set.isCompleted || set.durationS === null || set.durationS <= 0) continue;
    totalSeconds += set.durationS;
    countedSets++;
  }

  return { totalSeconds, countedSets, excludedWarmups };
}

/** Convert gram-reps to kilogram-reps for display. */
export function toKilogramReps(volumeGramReps: number): number {
  return volumeGramReps / 1000;
}

/** Heaviest single-set volume, used for the `max_volume_set` record. */
export function bestSetVolume(sets: readonly LoggedSet[]): { setId: string; volume: number } | null {
  let best: { setId: string; volume: number } | null = null;

  for (const set of sets) {
    const volume = setVolumeGramReps(set);
    if (volume === null) continue;
    if (best === null || volume > best.volume) {
      best = { setId: set.id, volume };
    }
  }

  return best;
}
